/**
 * COLUMN-PERSIST-1 — shared (per-project) column visibility preferences.
 *
 * One boolean per column under a namespaced key in `projects.column_config`
 * (`col:priority: true` = shown, `false` = hidden). Absent = "use the
 * methodology default", so defaults are computed on read and nothing is written
 * until someone changes a column. The backend allow-list
 * (zephix-backend/src/modules/projects/helpers/column-config-patch.ts) accepts
 * exactly these keys; column-prefs.drift.gating.test.ts keeps the two in step.
 */
import type { ProjectColumnKey } from './column-types';

export const COLUMN_PREF_PREFIX = 'col:';

/** Columns whose visibility is never a stored preference (see backend file). */
export const NON_PREFERENCE_COLUMN_KEYS: readonly ProjectColumnKey[] = [
  'title',
  'approvalStatus',
  'documentRequired',
];

export function columnPrefKey(key: ProjectColumnKey): string {
  return `${COLUMN_PREF_PREFIX}${key}`;
}

export function isPreferenceColumn(key: ProjectColumnKey): boolean {
  return !NON_PREFERENCE_COLUMN_KEYS.includes(key);
}

/** Stored explicit choices: true = shown, false = hidden. */
export function readColumnPrefs(
  columns: Record<string, boolean> | null | undefined,
): Partial<Record<ProjectColumnKey, boolean>> {
  const out: Partial<Record<ProjectColumnKey, boolean>> = {};
  if (!columns) return out;
  for (const [k, v] of Object.entries(columns)) {
    if (k.startsWith(COLUMN_PREF_PREFIX) && typeof v === 'boolean') {
      out[k.slice(COLUMN_PREF_PREFIX.length) as ProjectColumnKey] = v;
    }
  }
  return out;
}

/** Defaults overlaid with stored choices. `title` is never hidden. */
export function applyColumnPrefs(
  defaultHidden: Iterable<ProjectColumnKey>,
  prefs: Partial<Record<ProjectColumnKey, boolean>>,
): Set<ProjectColumnKey> {
  const hidden = new Set<ProjectColumnKey>(defaultHidden);
  for (const [k, shown] of Object.entries(prefs) as [ProjectColumnKey, boolean][]) {
    if (!isPreferenceColumn(k)) continue;
    if (shown) hidden.delete(k);
    else hidden.add(k);
  }
  hidden.delete('title');
  return hidden;
}
